import { existsSync, mkdirSync, writeFileSync } from "node:fs";
import { homedir } from "node:os";
import { join } from "node:path";
import type { CorrectionCluster } from "./corrections.js";
import { suggestRule } from "./corrections.js";

export type SkillProvider = "cai" | "claude" | "codex";
export type SkillScope = "project" | "personal";

export interface SkillTarget {
  provider: SkillProvider;
  scope: SkillScope;
  dir: string;
  path: string;
}

export interface WriteLearnedSkillOptions {
  projectRoot: string;
  scaffoldRoot: string;
  providers?: SkillProvider[];
  scope?: SkillScope;
  overwrite?: boolean;
}

export interface WriteLearnedSkillResult {
  name: string;
  content: string;
  targets: Array<SkillTarget & { written: boolean; skipped: boolean }>;
}

export function skillNameForCluster(cluster: CorrectionCluster): string {
  const base = slugify(cluster.signature || cluster.example);
  return `cai-learned-${base || cluster.id}`;
}

export function buildLearnedSkill(cluster: CorrectionCluster): { name: string; content: string } {
  const name = skillNameForCluster(cluster);
  const title = toTitle(cluster.example.replace(/[.!?]+$/, ""));
  const rule = suggestRule(cluster);
  const description = [
    `Use when this recurring project preference applies: ${cluster.example}.`,
    "Generated by CAI from repeated local prompt corrections.",
  ].join(" ");

  return {
    name,
    content: `---
name: ${yamlString(name)}
description: ${yamlString(description)}
metadata:
  cai:
    source: learned-correction
    cluster_id: ${yamlString(cluster.id)}
    occurrences: ${cluster.count}
    signal: ${yamlString(cluster.signal)}
---

# ${title}

Follow this recurring user correction:

${rule}

This skill came from ${cluster.count} repeated local prompt corrections. Keep the rule scoped to the current task unless the surrounding project instructions make it global.
`,
  };
}

export function resolveSkillTargets(
  projectRoot: string,
  scaffoldRoot: string,
  name: string,
  providers: SkillProvider[],
  scope: SkillScope = "project"
): SkillTarget[] {
  const roots = providers.map((provider): { provider: SkillProvider; root: string } => {
    if (scope === "personal") {
      if (provider === "cai") return { provider, root: join(caiHome(), "skills") };
      if (provider === "claude") return { provider, root: join(homedir(), ".claude", "skills") };
      return { provider, root: join(codexHome(), "skills") };
    }

    if (provider === "cai") return { provider, root: join(scaffoldRoot, "skills") };
    if (provider === "claude") return { provider, root: join(projectRoot, ".claude", "skills") };
    return { provider, root: join(projectRoot, ".codex", "skills") };
  });

  return roots.map(({ provider, root }) => {
    const dir = join(root, name);
    return {
      provider,
      scope,
      dir,
      path: join(dir, "SKILL.md"),
    };
  });
}

export function writeLearnedSkill(cluster: CorrectionCluster, opts: WriteLearnedSkillOptions): WriteLearnedSkillResult {
  const { name, content } = buildLearnedSkill(cluster);
  const providers: SkillProvider[] = opts.providers && opts.providers.length > 0 ? uniqueProviders(opts.providers) : ["cai"];
  const targets = resolveSkillTargets(opts.projectRoot, opts.scaffoldRoot, name, providers, opts.scope);

  const writtenTargets: WriteLearnedSkillResult["targets"] = [];
  for (const target of targets) {
    if (existsSync(target.path) && !opts.overwrite) {
      writtenTargets.push({ ...target, written: false, skipped: true });
      continue;
    }

    mkdirSync(target.dir, { recursive: true });
    writeFileSync(target.path, content, "utf8");
    writtenTargets.push({ ...target, written: true, skipped: false });
  }

  return {
    name,
    content,
    targets: writtenTargets,
  };
}

export function parseSkillProviders(value?: string): SkillProvider[] {
  if (!value || value === "cai") return ["cai"];
  const parts = value.split(",").map((p) => p.trim()).filter(Boolean);
  const expanded: string[] = parts.includes("all") ? ["cai", "claude", "codex"] : parts;
  const providers = expanded.map((p) => {
    if (p === "cai" || p === "claude" || p === "codex") return p;
    throw new Error(`Unknown skill provider "${p}". Use cai, claude, codex, or all.`);
  });
  return uniqueProviders(providers);
}

function uniqueProviders(providers: SkillProvider[]): SkillProvider[] {
  return Array.from(new Set(providers));
}

function codexHome(): string {
  return process.env.CODEX_HOME ?? join(homedir(), ".codex");
}

function caiHome(): string {
  return process.env.CAI_HOME ?? join(homedir(), ".cai");
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48)
    .replace(/-+$/g, "");
}

function toTitle(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "Learned Correction";
  return trimmed
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function yamlString(value: string): string {
  return JSON.stringify(value);
}
